// src/pages/Auditorias/components/ProcessosAlteradosDialog.js
import React, { useState, useCallback } from 'react';
import { listarProcessosAlterados } from '../api';
import { getProcessDetail } from '../../ClientData/api';
import { ESTADO_AUDITORIA_LABEL, formatarRelativo } from '../utils/formatacao';

/**
 * Processos com movimento na DuraLex num intervalo de datas, com a opção de
 * mandar auditar os que interessarem.
 *
 * A lista é paginada do lado da DuraLex (formato Laravel, 50 por página) e cada
 * pesquisa é UM pedido: são ~1600 processos num trimestre e ir buscar as páginas
 * todas de seguida bate no rate-limit da API.
 *
 * Cada linha diz se o processo já tem auditoria — é isso que decide a acção:
 * "auditar" abre uma auditoria nova (modo completa), "atualizar" corre o modo
 * incremental sobre a que já existe.
 */

const DIAS_POR_OMISSAO = 30;

function isoHoje() {
  return new Date().toISOString().slice(0, 10);
}

function isoHaDias(dias) {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
}

/** "2026-04-16 18:40:24" -> "16/04/2026 18:40" */
function formatarDataU(valor) {
  if (!valor) return '—';
  const encaixe = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(valor));
  if (!encaixe) return String(valor);
  const [, ano, mes, dia, hora, min] = encaixe;
  return `${dia}/${mes}/${ano} ${hora}:${min}`;
}

const ProcessosAlteradosDialog = ({ userEmail, onFechar, onDisparar }) => {
  const [since, setSince] = useState(isoHaDias(DIAS_POR_OMISSAO));
  const [until, setUntil] = useState(isoHoje());

  const [processos, setProcessos] = useState(null);   // null = ainda não pesquisou
  const [paginacao, setPaginacao] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // Estado por linha: qual está a disparar e o que já foi enfileirado nesta
  // sessão do diálogo. Sem isto, carregar duas vezes no mesmo botão mandava
  // dois pedidos e o segundo voltava 409.
  const [aDisparar, setADisparar] = useState(null);
  const [enfileirados, setEnfileirados] = useState({});

  const pesquisar = useCallback(async (pagina = 1) => {
    setCarregando(true);
    setErro(null);

    try {
      const { data } = await listarProcessosAlterados({
        since,
        until: until || undefined,
        pagina,
      });
      setProcessos(data.data?.processos || []);
      setPaginacao(data.data?.paginacao || null);
    } catch (e) {
      setProcessos(null);
      setPaginacao(null);
      setErro(e.response?.data?.message || 'Erro ao pesquisar processos alterados');
    } finally {
      setCarregando(false);
    }
  }, [since, until]);

  const submeter = (e) => {
    e.preventDefault();
    setEnfileirados({});
    pesquisar(1);
  };

  /**
   * Dispara a auditoria de uma linha.
   *
   * O /ai/processes/changed só devolve o código do processo, por isso o nome do
   * cliente e o nº judicial vêm de um pedido ao detalhe do processo — um pedido,
   * só quando se carrega no botão. Se falhar, a auditoria segue na mesma: são
   * campos de apresentação, não são precisos para o motor correr.
   */
  const auditar = async (processo) => {
    const cod = processo.cod_processo;

    if (!processo.cod_cli) {
      setErro(`Não foi possível deduzir o código do cliente de ${cod}. Use "Nova auditoria".`);
      return;
    }

    setADisparar(cod);
    setErro(null);

    let nomeCliente = processo.nome_cliente || null;
    let numProcesso = processo.num_processo || null;

    if (!nomeCliente || !numProcesso) {
      try {
        const { data } = await getProcessDetail(cod);
        const detalhe = data.data?.data || data.data || {};
        nomeCliente = nomeCliente || detalhe['Nome'] || detalhe['Cliente'] || null;
        numProcesso = numProcesso || detalhe['Nº Processo'] || detalhe['No Processo'] || null;
      } catch {
        // Detalhe indisponível — segue sem os rótulos
      }
    }

    try {
      await onDisparar({
        cod_cli: processo.cod_cli,
        cod_processo: cod,
        user_email: processo.user_email || userEmail,
        modo: processo.auditoria_id ? 'atualizacao' : 'completa',
        nome_cliente: nomeCliente,
        num_processo: numProcesso,
      });
      setEnfileirados((e) => ({ ...e, [cod]: 'enfileirada' }));
    } catch (e) {
      setEnfileirados((prev) => ({
        ...prev,
        [cod]: e.response?.status === 409 ? 'já na fila' : 'falhou',
      }));
      if (e.response?.status !== 409) {
        setErro(e.response?.data?.message || `Erro ao enfileirar ${cod}`);
      }
    } finally {
      setADisparar(null);
    }
  };

  const paginaActual = paginacao?.pagina || 1;
  const totalPaginas = paginacao?.total_paginas || 1;

  return (
    <div className="aud-overlay" onClick={onFechar}>
      <div className="aud-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aud-modal-cabecalho">
          <h3>Atualizações de processos</h3>
          <button className="aud-modal-fechar" onClick={onFechar} aria-label="Fechar">✖</button>
        </div>

        <div className="aud-modal-corpo">
          <form onSubmit={submeter} className="aud-form-datas">
            <label className="aud-campo-data">
              <span>De</span>
              <input
                type="date"
                value={since}
                max={until || isoHoje()}
                onChange={(e) => setSince(e.target.value)}
                required
              />
            </label>
            <label className="aud-campo-data">
              <span>Até</span>
              <input
                type="date"
                value={until}
                min={since}
                onChange={(e) => setUntil(e.target.value)}
              />
            </label>
            <button type="submit" className="aud-btn-primario" disabled={carregando || !since}>
              {carregando ? 'A PESQUISAR…' : 'PESQUISAR'}
            </button>
          </form>

          {erro && <p className="aud-erro">{erro}</p>}

          {carregando && <p className="aud-vazio">A consultar a DuraLex…</p>}

          {!carregando && processos !== null && processos.length === 0 && (
            <p className="aud-vazio">Nenhum processo com movimento neste intervalo.</p>
          )}

          {!carregando && processos !== null && processos.length > 0 && (
            <>
              <p className="aud-sub aud-resumo-alterados">
                {paginacao?.total} processo{paginacao?.total === 1 ? '' : 's'} alterado
                {paginacao?.total === 1 ? '' : 's'} no intervalo
                {totalPaginas > 1 && <> · página {paginaActual} de {totalPaginas}</>}
              </p>

              <table className="aud-tabela aud-tabela-compacta">
                <thead>
                  <tr>
                    <th>Processo</th>
                    <th>Alterado em</th>
                    <th>Auditoria</th>
                    <th aria-label="Acções" />
                  </tr>
                </thead>
                <tbody>
                  {processos.map((p) => {
                    const estadoLinha = enfileirados[p.cod_processo];
                    const ocupado = aDisparar === p.cod_processo;
                    const naFila = p.tem_job_activo || estadoLinha === 'enfileirada' || estadoLinha === 'já na fila';

                    return (
                      <tr key={p.cod_processo}>
                        <td>
                          <span className="aud-mono aud-cod-processo">{p.cod_processo}</span>
                          {p.nome_cliente && <div className="aud-sub">{p.nome_cliente}</div>}
                        </td>

                        <td className="aud-sub">{formatarDataU(p.data_alteracao)}</td>

                        <td>
                          {p.auditoria_id ? (
                            <>
                              <span className={`aud-badge aud-estado-${p.auditoria_estado}`}>
                                {ESTADO_AUDITORIA_LABEL[p.auditoria_estado] || p.auditoria_estado}
                              </span>
                              <div className="aud-sub">
                                auditada {formatarRelativo(p.ultima_atualizacao)}
                              </div>
                            </>
                          ) : (
                            <span className="aud-sub">sem auditoria</span>
                          )}
                        </td>

                        <td className="aud-accoes">
                          {estadoLinha === 'enfileirada' ? (
                            <span className="aud-sub aud-sub-ok">✔ enfileirada</span>
                          ) : estadoLinha === 'já na fila' ? (
                            <span className="aud-sub aud-sub-alerta">já na fila</span>
                          ) : (
                            <button
                              type="button"
                              className="aud-link"
                              disabled={ocupado || naFila || !p.cod_cli}
                              title={
                                naFila ? 'Já tem uma execução na fila'
                                  : !p.cod_cli ? 'Código de cliente indeterminado'
                                  : p.auditoria_id ? 'Procurar movimentos novos'
                                  : 'Auditar este processo de raiz'
                              }
                              onClick={() => auditar(p)}
                            >
                              {ocupado ? 'a enfileirar…' : p.auditoria_id ? 'atualizar' : 'auditar'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {totalPaginas > 1 && (
                <div className="aud-paginacao">
                  <button
                    type="button"
                    className="aud-btn-secundario"
                    disabled={carregando || paginaActual <= 1}
                    onClick={() => pesquisar(paginaActual - 1)}
                  >
                    ‹ ANTERIOR
                  </button>
                  <span className="aud-sub">página {paginaActual} de {totalPaginas}</span>
                  <button
                    type="button"
                    className="aud-btn-secundario"
                    disabled={carregando || paginaActual >= totalPaginas}
                    onClick={() => pesquisar(paginaActual + 1)}
                  >
                    SEGUINTE ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="aud-modal-rodape">
          <button className="aud-btn-secundario" onClick={onFechar}>FECHAR</button>
        </div>
      </div>
    </div>
  );
};

export default ProcessosAlteradosDialog;
